import {
  LogOfferActivityDocument,
  LogOfferActivityBatchDocument,
  OfferActivityLogType,
  OfferActivityLogChannelType,
} from '@ocx/graphql';

import type { ApolloClient } from '@apollo/client';
import type { OfferImpressionTrackingContext } from './tracking.types';

const STORAGE_KEY = 'pending_impressions';
const FLUSH_DEBOUNCE_MS = 1000;

const buildInput = (ctx: OfferImpressionTrackingContext, type: OfferActivityLogType) => ({
  channelType: OfferActivityLogChannelType.App,
  activityAt: new Date(),
  activityType: type,
  trackingId: ctx.trackingId,
  isActivityLogIncludedInAltriaDDM: ctx.isActivityLogIncludedInAltriaDDM,
  activateOfferId: ctx.activateOfferId,
});

export class ImpressionTrackingService {
  private static pendingImpressions: Map<string, OfferImpressionTrackingContext> = new Map();
  private static debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private static client: ApolloClient<unknown> | null = null;

  static init(client: ApolloClient<unknown>): void {
    if (this.client) {
      return;
    }
    this.client = client;
    this.restoreFromStorage();
  }

  static trackImpression(ctx: OfferImpressionTrackingContext): void {
    if (this.pendingImpressions.has(ctx.activateOfferId)) {
      return;
    }
    this.pendingImpressions.set(ctx.activateOfferId, ctx);
    this.persistToStorage();
    this.scheduleFlush();
  }

  static trackClick(ctx: OfferImpressionTrackingContext): void {
    this.pendingImpressions.delete(ctx.activateOfferId);
    this.persistToStorage();

    if (this.pendingImpressions.size === 0 && this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.send([ctx], OfferActivityLogType.Click);
  }

  private static scheduleFlush(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => this.flush(), FLUSH_DEBOUNCE_MS);
  }

  private static flush(): void {
    this.debounceTimer = null;
    if (this.pendingImpressions.size === 0) {
      return;
    }
    const snapshot = Array.from(this.pendingImpressions.values());
    this.send(snapshot, OfferActivityLogType.Impression, {
      onSuccess: () => {
        snapshot.forEach((ctx) => this.pendingImpressions.delete(ctx.activateOfferId));
        this.persistToStorage();
      },
    });
  }

  private static send(
    contexts: OfferImpressionTrackingContext[],
    type: OfferActivityLogType,
    { onSuccess }: { onSuccess?: () => void } = {},
  ): void {
    if (!this.client) {
      return;
    }
    const input = contexts.map((ctx) => buildInput(ctx, type));
    const mutation =
      input.length === 1
        ? this.client.mutate({ mutation: LogOfferActivityDocument, variables: { input: input[0] } })
        : this.client.mutate({ mutation: LogOfferActivityBatchDocument, variables: { input } });

    mutation.then(onSuccess).catch(() => {});
  }

  private static persistToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.pendingImpressions.values())));
    } catch {
      // storage unavailable — continue without persistence
    }
  }

  private static restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      const data: OfferImpressionTrackingContext[] = JSON.parse(raw);
      data.forEach((ctx) => this.pendingImpressions.set(ctx.activateOfferId, ctx));
      if (this.pendingImpressions.size > 0) {
        this.scheduleFlush();
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /** For use in tests only — resets all static state between test cases. */
  static resetForTesting(): void {
    this.pendingImpressions.clear();
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.client = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}
